import {
  Box,
  createListCollection,
  Field,
  Flex,
  Heading,
  Input,
  Portal,
  Select,
  Spinner,
  Text,
} from '@chakra-ui/react';
import { apiFetch } from 'helpers/api';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useRef, useState } from 'react';
import { LuInfo, LuUpload, LuX } from 'react-icons/lu';

import type {
  BusinessType,
  IdentificationType,
  IdRequirement,
} from '@features/onboarding/kycTypes';
import {
  areRequirementsMet,
  BUSINESS_TYPE_LABELS,
  BusinessInfoFormState,
  emptyBusinessInfoFormState,
  ID_TYPE_LABELS,
} from '@features/onboarding/kycTypes';

const BUSINESS_TYPES = createListCollection({
  items: (Object.entries(BUSINESS_TYPE_LABELS) as [BusinessType, string][]).map(
    ([value, label]) => ({ value, label }),
  ),
});

const isSoleProp = (t: BusinessType | '') => t === 'SOLE_PROPRIETORSHIP';

const isFormValid = (state: BusinessInfoFormState, requirements: IdRequirement[]): boolean => {
  if (state.businessName.trim() === '' || state.businessType === '') {
    return false;
  }
  if (Object.values(state.errors).some((v) => v != null)) {
    return false;
  }
  if (isSoleProp(state.businessType)) {
    return true;
  }
  return areRequirementsMet(requirements, state.documentUploads);
};

interface Props {
  country: string;
  onFormStateChange: (state: BusinessInfoFormState, isValid: boolean) => void;
}

const BusinessInfoStep = ({ country, onFormStateChange }: Props) => {
  const [formState, setFormState] = useState<BusinessInfoFormState>(emptyBusinessInfoFormState());
  const [orgRequirements, setOrgRequirements] = useState<IdRequirement[]>([]);
  const [loadingRequirements, setLoadingRequirements] = useState(false);
  const [anyOfSelections, setAnyOfSelections] = useState<Record<number, IdentificationType>>({});
  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  const updateState = useCallback(
    (nextState: BusinessInfoFormState) => {
      setFormState(nextState);
      onFormStateChange(nextState, isFormValid(nextState, orgRequirements));
    },
    [onFormStateChange, orgRequirements],
  );

  const fetchOrgRequirements = useCallback(
    async (businessType: BusinessType) => {
      if (country === '' || isSoleProp(businessType)) {
        setOrgRequirements([]);
        return;
      }
      setLoadingRequirements(true);
      try {
        const reqs = await apiFetch<IdRequirement[]>(
          `/kyc/id-requirements?entityType=ORG&businessType=${businessType}&country=${country}`,
        );
        setOrgRequirements(reqs);
      } catch {
        setOrgRequirements([]);
      } finally {
        setLoadingRequirements(false);
      }
    },
    [country],
  );

  const onChangeBusinessName = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      updateState({
        ...formState,
        businessName: value,
        errors: {
          ...formState.errors,
          businessName: value.trim() ? undefined : 'Business name is required',
        },
      });
    },
    [formState, updateState],
  );

  const onChangeBusinessType = useCallback(
    (details: { value: string[] }) => {
      const businessType = (details.value[0] ?? '') as BusinessType | '';
      const nextState: BusinessInfoFormState = {
        ...formState,
        businessType,
        documentUploads: {},
        errors: {
          ...formState.errors,
          businessType: businessType ? undefined : 'Business type is required',
          documents: undefined,
        },
      };
      setFormState(nextState);
      setAnyOfSelections({});
      // Sole prop needs no org documents — can be valid immediately.
      // Non-sole-prop will need docs, so mark invalid until requirements load and docs are uploaded.
      const valid = isSoleProp(businessType) && nextState.businessName.trim() !== '';
      onFormStateChange(nextState, valid);
      if (businessType !== '' && !isSoleProp(businessType)) {
        fetchOrgRequirements(businessType as BusinessType);
      } else {
        setOrgRequirements([]);
      }
    },
    [formState, onFormStateChange, fetchOrgRequirements],
  );

  const onAnyOfSelect = useCallback(
    (groupIndex: number, details: { value: string[] }) => {
      const docType = details.value[0] as IdentificationType | undefined;
      if (docType == null) {
        return;
      }
      const prev = anyOfSelections[groupIndex];
      const nextUploads = { ...formState.documentUploads };
      if (prev && prev !== docType) {
        delete nextUploads[prev];
      }
      setAnyOfSelections((s) => ({ ...s, [groupIndex]: docType }));
      updateState({ ...formState, documentUploads: nextUploads });
    },
    [formState, anyOfSelections, updateState],
  );

  const onFileUpload = useCallback(
    (docType: IdentificationType, e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file == null) {
        return;
      }
      updateState({
        ...formState,
        documentUploads: { ...formState.documentUploads, [docType]: file },
      });
    },
    [formState, updateState],
  );

  const onRemoveFile = useCallback(
    (docType: IdentificationType) => {
      const nextUploads = { ...formState.documentUploads };
      delete nextUploads[docType];
      updateState({ ...formState, documentUploads: nextUploads });
      const ref = fileInputRefs.current[docType];
      if (ref != null) {
        ref.value = '';
      }
    },
    [formState, updateState],
  );

  const setFileInputRef = useCallback((docType: string, el: HTMLInputElement | null) => {
    fileInputRefs.current[docType] = el;
  }, []);

  const requiresDocUpload = !isSoleProp(formState.businessType) && formState.businessType !== '';
  const hasRequirements = orgRequirements.some((r) => (r.idTypes?.length ?? 0) > 0);

  return (
    <Flex direction="column" gap={6} maxW="lg">
      <Box>
        <Heading size="md">Business Information</Heading>
        <Text color="text.secondary" fontSize="sm" fontWeight="medium" mt={1}>
          Tell us about your business to complete verification.
        </Text>
      </Box>

      <Flex direction="column" gap={5}>
        <Field.Root invalid={!!formState.errors.businessName}>
          <Field.Label>Business Name</Field.Label>
          <Input
            placeholder="Enter your legal business name"
            value={formState.businessName}
            borderColor="border.light"
            onChange={onChangeBusinessName}
          />
          <Field.ErrorText>{formState.errors.businessName}</Field.ErrorText>
        </Field.Root>

        <Field.Root invalid={!!formState.errors.businessType}>
          <Field.Label>Business Type</Field.Label>
          <Select.Root collection={BUSINESS_TYPES} onValueChange={onChangeBusinessType} size="md">
            <Select.HiddenSelect />
            <Select.Control>
              <Select.Trigger borderColor="border.light">
                <Select.ValueText placeholder="Select business type" />
              </Select.Trigger>
              <Select.IndicatorGroup>
                <Select.Indicator />
              </Select.IndicatorGroup>
            </Select.Control>
            <Portal>
              <Select.Positioner>
                <Select.Content bg="surface.background">
                  {BUSINESS_TYPES.items.map((item) => (
                    <Select.Item item={item} key={item.value}>
                      {item.label}
                      <Select.ItemIndicator />
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Positioner>
            </Portal>
          </Select.Root>
          <Field.ErrorText>{formState.errors.businessType}</Field.ErrorText>
        </Field.Root>

        {isSoleProp(formState.businessType) && (
          <Flex
            gap={3}
            p={4}
            borderRadius="md"
            bg="surface.muted"
            borderColor="border.light"
            borderWidth={1}
            align="flex-start"
          >
            <Box color="text.secondary" flexShrink={0} mt={0.5}>
              <LuInfo size={16} />
            </Box>
            <Box>
              <Text fontSize="sm" fontWeight="medium">
                No additional documents needed
              </Text>
              <Text fontSize="xs" color="text.secondary" fontWeight="medium" mt={0.5}>
                Your personal identity documents are sufficient for a sole proprietorship — no
                additional business documents required.
              </Text>
            </Box>
          </Flex>
        )}

        {requiresDocUpload && (
          <Flex direction="column" gap={4}>
            <Text fontWeight="medium" fontSize="sm">
              Business Documents
            </Text>

            {loadingRequirements && (
              <Flex align="center" gap={2}>
                <Spinner size="xs" />
                <Text fontSize="xs" color="text.secondary" fontWeight="medium">
                  Loading document requirements...
                </Text>
              </Flex>
            )}

            {!loadingRequirements &&
              hasRequirements &&
              orgRequirements.map((req, groupIndex) => {
                const types = req.idTypes ?? [];
                if (types.length === 0) {
                  return null;
                }

                if (req.condition === 'ANY_OF') {
                  const collection = createListCollection({
                    items: types.map((t) => ({ value: t, label: ID_TYPE_LABELS[t] ?? t })),
                  });
                  const selectedType = anyOfSelections[groupIndex];
                  const uploadedFile = selectedType
                    ? formState.documentUploads[selectedType]
                    : undefined;

                  return (
                    <Flex key={groupIndex} direction="column" gap={3}>
                      <Text fontSize="xs" color="text.secondary" fontWeight="medium">
                        Upload any one of the following:
                      </Text>

                      <Field.Root>
                        <Field.Label fontSize="sm">Document Type</Field.Label>
                        <Select.Root
                          collection={collection}
                          onValueChange={(d) => onAnyOfSelect(groupIndex, d)}
                          size="md"
                        >
                          <Select.HiddenSelect />
                          <Select.Control>
                            <Select.Trigger borderColor="border.light">
                              <Select.ValueText placeholder="Choose which document to upload" />
                            </Select.Trigger>
                            <Select.IndicatorGroup>
                              <Select.Indicator />
                            </Select.IndicatorGroup>
                          </Select.Control>
                          <Portal>
                            <Select.Positioner>
                              <Select.Content bg="surface.background">
                                {collection.items.map((item) => (
                                  <Select.Item item={item} key={item.value}>
                                    {item.label}
                                    <Select.ItemIndicator />
                                  </Select.Item>
                                ))}
                              </Select.Content>
                            </Select.Positioner>
                          </Portal>
                        </Select.Root>
                      </Field.Root>

                      {selectedType && (
                        <DocUploadField
                          docType={selectedType}
                          file={uploadedFile}
                          onFileChange={(e) => onFileUpload(selectedType, e)}
                          onRemove={() => onRemoveFile(selectedType)}
                          setRef={(el) => setFileInputRef(selectedType, el)}
                        />
                      )}
                    </Flex>
                  );
                }

                // ALL_OF: one upload per type
                return (
                  <Flex key={groupIndex} direction="column" gap={3}>
                    <Text fontSize="xs" color="text.secondary" fontWeight="medium">
                      All of the following documents are required:
                    </Text>
                    {types.map((docType) => (
                      <DocUploadField
                        key={docType}
                        label={ID_TYPE_LABELS[docType] ?? docType}
                        docType={docType}
                        file={formState.documentUploads[docType]}
                        onFileChange={(e) => onFileUpload(docType, e)}
                        onRemove={() => onRemoveFile(docType)}
                        setRef={(el) => setFileInputRef(docType, el)}
                      />
                    ))}
                  </Flex>
                );
              })}
          </Flex>
        )}
      </Flex>
    </Flex>
  );
};

interface DocUploadFieldProps {
  label?: string;
  docType: IdentificationType;
  file: File | undefined;
  onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemove: () => void;
  setRef: (el: HTMLInputElement | null) => void;
}

const DocUploadField = ({
  label,
  docType,
  file,
  onFileChange,
  onRemove,
  setRef,
}: DocUploadFieldProps) => {
  const inputRef = useRef<HTMLInputElement | null>(null);

  const handleRefSet = useCallback(
    (el: HTMLInputElement | null) => {
      inputRef.current = el;
      setRef(el);
    },
    [setRef],
  );

  return (
    <Field.Root w="full">
      {label && <Field.Label fontSize="sm">{label}</Field.Label>}
      <input
        ref={handleRefSet}
        type="file"
        accept=".pdf,.png,.jpg,.jpeg,.docx"
        style={{ display: 'none' }}
        onChange={onFileChange}
      />
      {file ? (
        <Flex
          w="full"
          align="center"
          justify="space-between"
          p={3}
          borderWidth={1}
          borderStyle="solid"
          borderColor="border.light"
          borderRadius="md"
          bg="surface.muted"
        >
          <Text fontSize="sm" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
            {file.name}
          </Text>
          <Box
            as="button"
            color="text.secondary"
            onClick={onRemove}
            _hover={{ color: 'text.primary' }}
            ml={2}
            flexShrink={0}
          >
            <LuX size={14} />
          </Box>
        </Flex>
      ) : (
        <Flex
          w="full"
          direction="column"
          align="center"
          justify="center"
          gap={2}
          p={5}
          borderWidth={2}
          borderStyle="dashed"
          borderColor="border.light"
          borderRadius="lg"
          cursor="pointer"
          onClick={() => inputRef.current?.click()}
          _hover={{ borderColor: 'border.dark', bg: 'surface.hover' }}
          transition="all 0.2s"
        >
          <Box color="text.secondary">
            <LuUpload size={18} />
          </Box>
          <Text fontSize="sm" fontWeight="medium">
            Click to upload {label ?? ID_TYPE_LABELS[docType] ?? 'document'}
          </Text>
          <Text fontSize="xs" color="text.secondary" fontWeight="medium">
            PDF, PNG, JPG, or DOCX — up to 50 MB
          </Text>
        </Flex>
      )}
    </Field.Root>
  );
};

export default observer(BusinessInfoStep);
